import axios, { AxiosResponse, ResponseType } from 'axios';
// import qs from 'qs';
import lockr from 'lockr';
import { isUndefined, isEmpty, isObject } from 'underscore';
import appConstants from '../constants';
import { recordActivity, clearSessionAuth, isSessionExpired } from './session';
// load mock data if enabled
import './mock';

const {
    API_URL,
    AUTH_KEY,
    BACKEND,
    GOOGLE_MAPS_KEY,
} = appConstants;

// ----- Helpers ----- //
// Get Api Url
const getApiUrl = () => `${API_URL}/api`;

// Get hostname url
const getBaseUrl = () => API_URL;

// defaults for axios
const api = axios.create({
    baseURL: getApiUrl(),
});

// Get locational data
const locationData = (name:string) => axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${name}&key=${GOOGLE_MAPS_KEY}`);

interface payloadProps {
    [key: string]: []|object|null|undefined|string|number;
}
// Format params for api call
const formatParams = (payload: payloadProps, key:number|string) => {
    let params = payload;
    if (!isObject(payload)) {
        params = {};
        params[key || 'id'] = payload;
    }
    return params;
};

// ----- Auth Key Helpers -----//

/**
 * Get Bearer token from storage
 * @return string
 */
const getAuthKey = () => lockr.get(AUTH_KEY);

/**
 * Get header object for auth token
 * @return object
 */
const getAuthHeaders = ():object => ({ Authorization: `Bearer ${getAuthKey()}` });

// Global handling for an expired/invalid session. When an authenticated
// request comes back 401 the local token is stale, so wipe all auth storage
// and hard-redirect to the login page. The full page load also clears any
// persisted Redux state, so no stale dashboard data is left on screen.
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        // Only force a logout when we believed we were authenticated. During
        // login / 2FA there is no token yet, so a 401 there (bad credentials)
        // is handled by the caller and must not trigger a redirect.
        if (status === 401 && !isUndefined(getAuthKey())) {
            clearSessionAuth();
            if (window.location.pathname !== '/') {
                window.location.href = '/';
            }
        }
        return Promise.reject(error);
    },
);

// ----- Api Functions ----- //
interface OptsProps {
    method: string;
    url: string;
    body?: object;
    params?: object;
    headers?: object;
    responseType?: ResponseType;
    data?: string|object|null|undefined;
    // Set on background/polling requests so they do NOT count as user activity
    // (otherwise polling would continually reset the idle-session timer).
    background?: boolean;
}
function fetchApi(opts:OptsProps, headers:object|null = null):Promise<AxiosResponse> {

    const data = (opts.method.toUpperCase() === 'GET') ? null : opts.body;
    const isFormData = data instanceof FormData;
    const processedData = data; // BACKEND === 'yii' && data ? qs.stringify(data) : data;

    // Don't set Content-Type for FormData - let browser handle it
    const laravelHeaders = BACKEND !== 'yii' ? {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        'X-Requested-With': 'XMLHttpRequest',
    } : {};

    const options:OptsProps = {
        method: opts.method,
        url: opts.url,
        responseType: opts.responseType || 'json',
        params: opts.params || {},
        headers: { ...laravelHeaders, ...opts.headers },
    };

    if (opts.method.toUpperCase() !== 'GET') {
        options.data = processedData;
    }

    if (!isEmpty(headers)) {
        options.headers = { ...options.headers, ...headers };
    }

    return api(options);
}

function fetchApiAuth(opts:OptsProps):Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
        if (isUndefined(getAuthKey())) {
            reject(new Error('not-authorised'));
            return;
        }
        // Background polls (notifications, session checks) never extend the
        // session (no recordActivity) and must stay silent: don't run the
        // client expiry check, don't reject, don't redirect an otherwise-idle
        // user. Expiry for an idle user is enforced on the next navigation
        // (ProtectedRoute), and the 401 interceptor remains the server backstop.
        if (opts.background) {
            resolve(fetchApi(opts, getAuthHeaders()));
            return;
        }
        // User-initiated request: enforce expiry BEFORE refreshing activity or
        // sending, otherwise a same-page action after the idle/absolute timeout
        // would extend a dead session and still hit the API.
        if (isSessionExpired()) {
            clearSessionAuth();
            if (window.location.pathname !== '/') {
                window.location.href = '/';
            }
            reject(new Error('session-expired'));
            return;
        }
        recordActivity();
        resolve(fetchApi(opts, getAuthHeaders()));
    });
}

export {
    getApiUrl,
    getBaseUrl,
    locationData,
    formatParams,
    // -- Auth Key Helpers --//
    getAuthKey,
    getAuthHeaders,

    fetchApi,
    fetchApiAuth,
};
